import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  ExternalLink, 
  Code2, 
  Key, 
  Shield,
  Server,
  Database,
  Zap,
  Copy,
  Check,
  Plus,
  Trash2,
  RefreshCw,
  Play,
  Share2,
  Download,
  Eye,
  EyeOff,
  Globe,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileJson,
  Webhook,
  FileText,
  Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

// Complete API documentation
const apiEndpoints = [
  {
    group: 'Autenticación',
    tag: 'auth',
    color: 'bg-blue-500',
    endpoints: [
      { method: 'POST', path: '/api/auth/login/pin', desc: 'Login con PIN de restaurante', auth: false },
      { method: 'POST', path: '/api/auth/set-pin', desc: 'Establecer PIN (primer login)', auth: true },
      { method: 'GET', path: '/api/auth/me', desc: 'Obtener usuario actual', auth: true },
    ]
  },
  {
    group: 'Restaurante',
    tag: 'restaurant',
    color: 'bg-orange-500',
    endpoints: [
      { method: 'GET', path: '/api/restaurant', desc: 'Obtener configuración del restaurante', auth: true },
      { method: 'PATCH', path: '/api/restaurant', desc: 'Actualizar configuración', auth: true },
      { method: 'POST', path: '/api/restaurant/logo', desc: 'Subir logo', auth: true },
    ]
  },
  {
    group: 'Ubicaciones',
    tag: 'locations',
    color: 'bg-purple-500',
    endpoints: [
      { method: 'GET', path: '/api/locations', desc: 'Listar ubicaciones accesibles', auth: true },
      { method: 'POST', path: '/api/locations', desc: 'Crear nueva ubicación', auth: true },
      { method: 'POST', path: '/api/locations/switch/{id}', desc: 'Cambiar ubicación activa', auth: true },
      { method: 'PATCH', path: '/api/locations/{id}', desc: 'Actualizar ubicación', auth: true },
      { method: 'DELETE', path: '/api/locations/{id}', desc: 'Eliminar ubicación', auth: true },
    ]
  },
  {
    group: 'Mesas',
    tag: 'tables',
    color: 'bg-green-500',
    endpoints: [
      { method: 'GET', path: '/api/tables', desc: 'Listar todas las mesas', auth: true },
      { method: 'POST', path: '/api/tables', desc: 'Crear mesa', auth: true },
      { method: 'GET', path: '/api/tables/{id}', desc: 'Obtener mesa específica', auth: true },
      { method: 'PATCH', path: '/api/tables/{id}', desc: 'Actualizar mesa', auth: true },
      { method: 'DELETE', path: '/api/tables/{id}', desc: 'Eliminar mesa', auth: true },
      { method: 'PATCH', path: '/api/tables/{id}/status', desc: 'Cambiar estado de mesa', auth: true },
    ]
  },
  {
    group: 'Zonas',
    tag: 'zones',
    color: 'bg-cyan-500',
    endpoints: [
      { method: 'GET', path: '/api/zones', desc: 'Listar zonas', auth: true },
      { method: 'POST', path: '/api/zones', desc: 'Crear zona', auth: true },
      { method: 'PATCH', path: '/api/zones/{id}', desc: 'Actualizar zona', auth: true },
      { method: 'DELETE', path: '/api/zones/{id}', desc: 'Eliminar zona', auth: true },
    ]
  },
  {
    group: 'Horarios',
    tag: 'time-slots',
    color: 'bg-amber-500',
    endpoints: [
      { method: 'GET', path: '/api/time-slots', desc: 'Listar horarios disponibles', auth: true },
      { method: 'POST', path: '/api/time-slots', desc: 'Crear horario', auth: true },
      { method: 'PATCH', path: '/api/time-slots/{id}', desc: 'Actualizar horario', auth: true },
      { method: 'DELETE', path: '/api/time-slots/{id}', desc: 'Eliminar horario', auth: true },
    ]
  },
  {
    group: 'Reservaciones',
    tag: 'reservations',
    color: 'bg-red-500',
    endpoints: [
      { method: 'GET', path: '/api/reservations', desc: 'Listar reservaciones (con filtros)', auth: true },
      { method: 'POST', path: '/api/reservations', desc: 'Crear reservación', auth: true },
      { method: 'GET', path: '/api/reservations/{id}', desc: 'Obtener reservación', auth: true },
      { method: 'PATCH', path: '/api/reservations/{id}', desc: 'Actualizar reservación', auth: true },
      { method: 'DELETE', path: '/api/reservations/{id}', desc: 'Cancelar reservación', auth: true },
      { method: 'PATCH', path: '/api/reservations/{id}/status', desc: 'Cambiar estado', auth: true },
      { method: 'PATCH', path: '/api/reservations/{id}/assign-table', desc: 'Asignar mesa', auth: true },
      { method: 'GET', path: '/api/reservations/export/csv', desc: 'Exportar a CSV', auth: true },
      { method: 'GET', path: '/api/reservations/export/pdf', desc: 'Exportar a PDF', auth: true },
    ]
  },
  {
    group: 'Clientes (CRM)',
    tag: 'customers',
    color: 'bg-pink-500',
    endpoints: [
      { method: 'GET', path: '/api/customers', desc: 'Listar clientes', auth: true },
      { method: 'POST', path: '/api/customers', desc: 'Crear cliente', auth: true },
      { method: 'GET', path: '/api/customers/{id}', desc: 'Obtener cliente', auth: true },
      { method: 'PATCH', path: '/api/customers/{id}', desc: 'Actualizar cliente', auth: true },
      { method: 'DELETE', path: '/api/customers/{id}', desc: 'Eliminar cliente', auth: true },
      { method: 'POST', path: '/api/customers/{id}/send-coupon', desc: 'Enviar cupón al cliente', auth: true },
      { method: 'GET', path: '/api/customers/export/csv', desc: 'Exportar a CSV', auth: true },
      { method: 'GET', path: '/api/customers/export/pdf', desc: 'Exportar a PDF', auth: true },
    ]
  },
  {
    group: 'Lista de Espera',
    tag: 'waitlist',
    color: 'bg-yellow-500',
    endpoints: [
      { method: 'GET', path: '/api/waitlist', desc: 'Listar cola de espera', auth: true },
      { method: 'POST', path: '/api/waitlist', desc: 'Agregar a lista de espera', auth: true },
      { method: 'POST', path: '/api/waitlist/{id}/notify', desc: 'Notificar disponibilidad', auth: true },
      { method: 'POST', path: '/api/waitlist/{id}/convert', desc: 'Convertir a reservación', auth: true },
      { method: 'DELETE', path: '/api/waitlist/{id}', desc: 'Eliminar de lista', auth: true },
    ]
  },
  {
    group: 'Eventos',
    tag: 'events',
    color: 'bg-fuchsia-500',
    endpoints: [
      { method: 'GET', path: '/api/events', desc: 'Listar eventos', auth: true },
      { method: 'POST', path: '/api/events', desc: 'Crear evento', auth: true },
      { method: 'GET', path: '/api/events/{id}', desc: 'Obtener evento', auth: true },
      { method: 'PATCH', path: '/api/events/{id}', desc: 'Actualizar evento', auth: true },
      { method: 'DELETE', path: '/api/events/{id}', desc: 'Eliminar evento', auth: true },
      { method: 'POST', path: '/api/events/{id}/tickets', desc: 'Vender boleto', auth: true },
      { method: 'GET', path: '/api/events/{id}/tickets', desc: 'Listar boletos', auth: true },
      { method: 'POST', path: '/api/events/tickets/{code}/checkin', desc: 'Check-in con código', auth: true },
    ]
  },
  {
    group: 'Marketing',
    tag: 'marketing',
    color: 'bg-indigo-500',
    endpoints: [
      { method: 'GET', path: '/api/marketing/promo-codes', desc: 'Listar códigos promocionales', auth: true },
      { method: 'POST', path: '/api/marketing/promo-codes', desc: 'Crear código promocional', auth: true },
      { method: 'PATCH', path: '/api/marketing/promo-codes/{id}', desc: 'Actualizar código', auth: true },
      { method: 'DELETE', path: '/api/marketing/promo-codes/{id}', desc: 'Eliminar código', auth: true },
      { method: 'POST', path: '/api/marketing/promo-codes/validate', desc: 'Validar código', auth: false },
    ]
  },
  {
    group: 'Analíticas',
    tag: 'analytics',
    color: 'bg-teal-500',
    endpoints: [
      { method: 'GET', path: '/api/analytics/overview', desc: 'Dashboard resumen', auth: true },
      { method: 'GET', path: '/api/analytics/daily', desc: 'Estadísticas por día', auth: true },
      { method: 'GET', path: '/api/analytics/monthly', desc: 'Estadísticas mensuales', auth: true },
    ]
  },
  {
    group: 'Predicciones ML',
    tag: 'predictions',
    color: 'bg-violet-500',
    endpoints: [
      { method: 'GET', path: '/api/predictions/no-show/{id}', desc: 'Predicción no-show para reservación', auth: true },
      { method: 'GET', path: '/api/predictions/high-risk', desc: 'Listar reservaciones de alto riesgo', auth: true },
      { method: 'POST', path: '/api/predictions/batch', desc: 'Predicciones en lote', auth: true },
      { method: 'GET', path: '/api/predictions/stats', desc: 'Estadísticas de no-shows', auth: true },
    ]
  },
  {
    group: 'Cumpleaños',
    tag: 'birthday',
    color: 'bg-rose-500',
    endpoints: [
      { method: 'GET', path: '/api/birthday/settings', desc: 'Obtener configuración', auth: true },
      { method: 'PUT', path: '/api/birthday/settings', desc: 'Actualizar configuración', auth: true },
      { method: 'GET', path: '/api/birthday/upcoming', desc: 'Próximos cumpleaños', auth: true },
      { method: 'POST', path: '/api/birthday/send-test/{id}', desc: 'Enviar mensaje de prueba', auth: true },
      { method: 'POST', path: '/api/birthday/process', desc: 'Procesar cumpleaños del día', auth: true },
      { method: 'GET', path: '/api/birthday/history', desc: 'Historial de mensajes', auth: true },
    ]
  },
  {
    group: 'Webhooks',
    tag: 'webhooks',
    color: 'bg-slate-500',
    endpoints: [
      { method: 'GET', path: '/api/webhooks', desc: 'Listar webhooks', auth: true },
      { method: 'POST', path: '/api/webhooks', desc: 'Crear webhook', auth: true },
      { method: 'GET', path: '/api/webhooks/{id}', desc: 'Obtener webhook', auth: true },
      { method: 'PATCH', path: '/api/webhooks/{id}', desc: 'Actualizar webhook', auth: true },
      { method: 'DELETE', path: '/api/webhooks/{id}', desc: 'Eliminar webhook', auth: true },
      { method: 'POST', path: '/api/webhooks/{id}/test', desc: 'Enviar evento de prueba', auth: true },
      { method: 'POST', path: '/api/webhooks/{id}/regenerate-secret', desc: 'Regenerar secreto', auth: true },
      { method: 'GET', path: '/api/webhooks/{id}/logs', desc: 'Ver logs de entregas', auth: true },
      { method: 'GET', path: '/api/webhooks/events', desc: 'Listar tipos de eventos', auth: false },
    ]
  },
  {
    group: 'Widget Público',
    tag: 'widget',
    color: 'bg-emerald-500',
    endpoints: [
      { method: 'GET', path: '/api/public/restaurant/{id}', desc: 'Info del restaurante', auth: false },
      { method: 'GET', path: '/api/public/availability/{id}', desc: 'Disponibilidad', auth: false },
      { method: 'POST', path: '/api/public/reservation/{id}', desc: 'Crear reservación pública', auth: false },
      { method: 'GET', path: '/api/public/events/restaurant/{id}', desc: 'Eventos públicos', auth: false },
      { method: 'GET', path: '/api/public/event/{slug}', desc: 'Evento por slug', auth: false },
      { method: 'POST', path: '/api/public/waitlist/{id}', desc: 'Registro en lista de espera', auth: false },
    ]
  },
  {
    group: 'OkWallet',
    tag: 'wallet',
    color: 'bg-violet-500',
    endpoints: [
      { method: 'GET', path: '/api/wallet/balance', desc: 'Saldo actual del wallet del restaurante', auth: true },
      { method: 'GET', path: '/api/wallet/transactions', desc: 'Historial de transacciones', auth: true },
      { method: 'POST', path: '/api/wallet/deposit', desc: 'Registrar depósito en wallet', auth: true },
      { method: 'POST', path: '/api/wallet/charge', desc: 'Cobrar del wallet del cliente', auth: true },
      { method: 'GET', path: '/api/wallet/customer/{customer_id}', desc: 'Saldo de un cliente específico', auth: true },
      { method: 'POST', path: '/api/wallet/transfer', desc: 'Transferir saldo entre wallets', auth: true },
    ]
  },
  {
    group: 'Menús & Cartas',
    tag: 'menus',
    color: 'bg-orange-600',
    endpoints: [
      { method: 'GET', path: '/api/menus', desc: 'Listar todos los menús del restaurante', auth: true },
      { method: 'POST', path: '/api/menus', desc: 'Crear nuevo menú', auth: true },
      { method: 'PUT', path: '/api/menus/{menu_id}', desc: 'Actualizar menú', auth: true },
      { method: 'DELETE', path: '/api/menus/{menu_id}', desc: 'Eliminar menú y sus platillos', auth: true },
      { method: 'GET', path: '/api/menus/{menu_id}/dishes', desc: 'Listar platillos de un menú', auth: true },
      { method: 'POST', path: '/api/menus/{menu_id}/dishes', desc: 'Agregar platillo al menú', auth: true },
      { method: 'PUT', path: '/api/menus/{menu_id}/dishes/{dish_id}', desc: 'Actualizar platillo', auth: true },
      { method: 'DELETE', path: '/api/menus/{menu_id}/dishes/{dish_id}', desc: 'Eliminar platillo', auth: true },
      { method: 'POST', path: '/api/menus/{menu_id}/dishes/reorder', desc: 'Reordenar platillos', auth: true },
      { method: 'GET', path: '/api/menus/public/{restaurant_id}', desc: 'Menús públicos del restaurante', auth: false },
      { method: 'POST', path: '/api/menus/presets/{preset_name}', desc: 'Crear menú desde plantilla', auth: true },
    ]
  },
  {
    group: 'Acceso Garantizado',
    tag: 'acceso-garantizado',
    color: 'bg-yellow-600',
    endpoints: [
      { method: 'GET', path: '/api/nuevas_funciones/acceso-garantizado/plans', desc: 'Listar planes de acceso garantizado', auth: true },
      { method: 'POST', path: '/api/nuevas_funciones/acceso-garantizado/plans', desc: 'Crear plan de membresía', auth: true },
      { method: 'PUT', path: '/api/nuevas_funciones/acceso-garantizado/plans/{plan_id}', desc: 'Actualizar plan', auth: true },
      { method: 'DELETE', path: '/api/nuevas_funciones/acceso-garantizado/plans/{plan_id}', desc: 'Eliminar plan', auth: true },
      { method: 'GET', path: '/api/nuevas_funciones/acceso-garantizado/members', desc: 'Listar miembros activos', auth: true },
      { method: 'POST', path: '/api/nuevas_funciones/acceso-garantizado/members', desc: 'Agregar miembro al plan', auth: true },
      { method: 'DELETE', path: '/api/nuevas_funciones/acceso-garantizado/members/{member_id}', desc: 'Cancelar membresía', auth: true },
    ]
  },
  {
    group: 'Email Marketing',
    tag: 'email-server',
    color: 'bg-sky-500',
    endpoints: [
      { method: 'GET', path: '/api/email-server/my-config', desc: 'Configuración de email del restaurante', auth: true },
      { method: 'POST', path: '/api/email-server/my-config', desc: 'Guardar config (nombre, email, reply-to)', auth: true },
      { method: 'GET', path: '/api/email-server/templates', desc: 'Listar plantillas HTML globales', auth: true },
      { method: 'POST', path: '/api/email-server/send-campaign', desc: 'Enviar campaña de email a lista', auth: true },
      { method: 'GET', path: '/api/email-server/campaigns', desc: 'Historial de campañas enviadas', auth: true },
      { method: 'POST', path: '/api/email-server/send-smtp', desc: 'Envío transaccional directo por SMTP', auth: true },
    ]
  },
  {
    group: 'Multi-Moneda',
    tag: 'currency',
    color: 'bg-teal-500',
    endpoints: [
      { method: 'GET', path: '/api/restaurant/currency', desc: 'Moneda configurada del restaurante', auth: true },
      { method: 'PATCH', path: '/api/restaurant/currency', desc: 'Cambiar moneda del restaurante (ISO 4217)', auth: true },
      { method: 'GET', path: '/api/public/currencies', desc: 'Lista de monedas soportadas', auth: false },
    ]
  },
];

const webhookEvents = [
  { event: 'reservation.created', desc: 'Nueva reservación creada' },
  { event: 'reservation.updated', desc: 'Reservación actualizada' },
  { event: 'reservation.cancelled', desc: 'Reservación cancelada' },
  { event: 'reservation.confirmed', desc: 'Reservación confirmada' },
  { event: 'reservation.seated', desc: 'Cliente sentado' },
  { event: 'reservation.completed', desc: 'Servicio completado' },
  { event: 'reservation.noshow', desc: 'Cliente no se presentó' },
  { event: 'customer.created', desc: 'Nuevo cliente registrado' },
  { event: 'customer.updated', desc: 'Cliente actualizado' },
  { event: 'waitlist.added', desc: 'Agregado a lista de espera' },
  { event: 'waitlist.notified', desc: 'Notificado de disponibilidad' },
  { event: 'waitlist.converted', desc: 'Convertido a reservación' },
  { event: 'event.created', desc: 'Nuevo evento creado' },
  { event: 'event.updated', desc: 'Evento actualizado' },
  { event: 'event.ticket_sold', desc: 'Boleto vendido' },
  { event: 'table.status_changed', desc: 'Estado de mesa cambiado' },
];

const methodColors = {
  GET: 'bg-green-500/10 text-green-600 border-green-500/20',
  POST: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  PATCH: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  PUT: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  DELETE: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export default function AdminAPIDocs() {
  const [copiedText, setCopiedText] = useState(null);
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookDescription, setWebhookDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [showSecret, setShowSecret] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    setLoadingWebhooks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data);
      }
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoadingWebhooks(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopiedText(null), 2000);
  };

  const handleCreateWebhook = async () => {
    if (!webhookUrl || selectedEvents.length === 0) {
      toast.error('URL y al menos un evento son requeridos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: webhookUrl,
          events: selectedEvents,
          description: webhookDescription,
          is_active: true
        })
      });

      if (response.ok) {
        toast.success('Webhook creado exitosamente');
        setShowWebhookDialog(false);
        setWebhookUrl('');
        setWebhookDescription('');
        setSelectedEvents([]);
        fetchWebhooks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear webhook');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!confirm('¿Eliminar este webhook?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Webhook eliminado');
        fetchWebhooks();
      }
    } catch (error) {
      toast.error('Error al eliminar webhook');
    }
  };

  const handleTestWebhook = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/webhooks/${id}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Prueba exitosa (${result.status_code})`);
      } else {
        toast.error(`Prueba fallida: ${result.error || result.status_code}`);
      }
    } catch (error) {
      toast.error('Error al probar webhook');
    }
  };

  const toggleEvent = (event) => {
    setSelectedEvents(prev => 
      prev.includes(event) 
        ? prev.filter(e => e !== event)
        : [...prev, event]
    );
  };

  const downloadOpenAPI = () => {
    window.open(`${API_URL}/api/openapi.json`, '_blank');
  };

  const shareDocumentation = () => {
    const docUrl = `${FRONTEND_URL}/docs/swagger`;
    navigator.clipboard.writeText(docUrl);
    toast.success('URL de documentación copiada');
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Title
      doc.setFontSize(24);
      doc.setTextColor(249, 115, 22); // Orange
      doc.text('OkReserva API', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Documentación Completa de la API', pageWidth / 2, 28, { align: 'center' });
      doc.text(`Generado: ${new Date().toLocaleDateString('es-MX')}`, pageWidth / 2, 35, { align: 'center' });
      
      // Base URL
      doc.setFontSize(10);
      doc.setTextColor(60);
      doc.text(`Base URL: ${API_URL}/api`, 14, 45);
      
      let yPosition = 55;
      
      // Endpoints table for each group
      apiEndpoints.forEach((group) => {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Group header
        doc.setFontSize(14);
        doc.setTextColor(30);
        doc.text(group.group, 14, yPosition);
        yPosition += 8;
        
        // Endpoints table
        const tableData = group.endpoints.map(ep => [
          ep.method,
          ep.path,
          ep.desc,
          ep.auth ? 'Sí' : 'No'
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          head: [['Método', 'Ruta', 'Descripción', 'Auth']],
          body: tableData,
          theme: 'striped',
          headStyles: { 
            fillColor: [249, 115, 22],
            textColor: 255,
            fontSize: 8
          },
          bodyStyles: { fontSize: 7 },
          columnStyles: {
            0: { cellWidth: 18 },
            1: { cellWidth: 55 },
            2: { cellWidth: 80 },
            3: { cellWidth: 15 }
          },
          margin: { left: 14, right: 14 }
        });
        
        yPosition = doc.lastAutoTable.finalY + 12;
      });
      
      // Add webhooks section
      doc.addPage();
      doc.setFontSize(18);
      doc.setTextColor(249, 115, 22);
      doc.text('Webhooks', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(60);
      doc.text('Eventos disponibles para suscripción:', 14, 35);
      
      const webhookData = webhookEvents.map(ev => [ev.event, ev.desc]);
      
      autoTable(doc, {
        startY: 40,
        head: [['Evento', 'Descripción']],
        body: webhookData,
        theme: 'striped',
        headStyles: { 
          fillColor: [249, 115, 22],
          textColor: 255,
          fontSize: 9
        },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 }
      });
    
    // Authentication section
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(249, 115, 22);
    doc.text('Autenticación', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(60);
    const authText = [
      'Método: JWT Bearer Token',
      '',
      'Header requerido:',
      'Authorization: Bearer <token>',
      '',
      'Obtener token:',
      'POST /api/auth/login/pin',
      '{ "restaurant_name": "...", "pin": "1234" }',
      '',
      'Roles disponibles:',
      '• super_admin - Acceso completo',
      '• owner - Dueño del restaurante',
      '• admin - Administrador de ubicación',
      '• manager - Gestión de reservaciones',
      '• staff - Operaciones básicas'
    ];
    
    let authY = 35;
    authText.forEach(line => {
      doc.text(line, 14, authY);
      authY += 6;
    });
    
    // URLs section
    doc.setFontSize(12);
    doc.setTextColor(30);
    doc.text('URLs de Documentación:', 14, authY + 10);
    
    doc.setFontSize(9);
    doc.setTextColor(60);
    doc.text(`Swagger UI: ${FRONTEND_URL}/docs/swagger`, 14, authY + 20);
    doc.text(`ReDoc: ${FRONTEND_URL}/docs/redoc`, 14, authY + 26);
    doc.text(`OpenAPI JSON: ${FRONTEND_URL}/docs/openapi`, 14, authY + 32);
    
      // Save
      doc.save('okreserva-api-documentation.pdf');
      toast.success('PDF exportado exitosamente');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar PDF: ' + error.message);
    }
  };

  const filteredEndpoints = apiEndpoints.filter(group => {
    if (selectedGroup !== 'all' && group.tag !== selectedGroup) return false;
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      group.group.toLowerCase().includes(query) ||
      group.endpoints.some(ep => 
        ep.path.toLowerCase().includes(query) ||
        ep.desc.toLowerCase().includes(query)
      )
    );
  });

  return (
    <div className="min-h-screen" data-testid="admin-api-docs-page">
      <header className="sticky top-0 z-30 h-14 glass flex items-center justify-between px-6">
        <h1 className="text-lg font-semibold">Documentación API</h1>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportToPDF} className="gap-1">
            <FileText className="h-4 w-4" /> Exportar PDF
          </Button>
          <Button size="sm" variant="outline" onClick={shareDocumentation} className="gap-1">
            <Share2 className="h-4 w-4" /> Compartir
          </Button>
          <Button size="sm" variant="outline" onClick={downloadOpenAPI} className="gap-1">
            <Download className="h-4 w-4" /> OpenAPI
          </Button>
          <a href={`${FRONTEND_URL}/docs/swagger`} target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="gap-1">
              <ExternalLink className="h-4 w-4" /> Swagger UI
            </Button>
          </a>
        </div>
      </header>

      <div className="p-6 space-y-6 fade-in">
        {/* Public URLs Banner */}
        <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Link2 className="h-5 w-5 text-orange-500" />
              <h3 className="font-semibold">URLs Públicas de Documentación</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-center gap-2 p-2 rounded bg-background/50">
                <Code2 className="h-4 w-4 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Swagger UI</p>
                  <code className="text-xs truncate block">{FRONTEND_URL}/docs/swagger</code>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(`${FRONTEND_URL}/docs/swagger`, 'swagger-url')}>
                  {copiedText === 'swagger-url' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
              <div className="flex items-center gap-2 p-2 rounded bg-background/50">
                <Server className="h-4 w-4 text-blue-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">ReDoc</p>
                  <code className="text-xs truncate block">{FRONTEND_URL}/docs/redoc</code>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(`${FRONTEND_URL}/docs/redoc`, 'redoc-url')}>
                  {copiedText === 'redoc-url' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
              <div className="flex items-center gap-2 p-2 rounded bg-background/50">
                <FileJson className="h-4 w-4 text-green-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">OpenAPI JSON</p>
                  <code className="text-xs truncate block">{FRONTEND_URL}/docs/openapi</code>
                </div>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(`${FRONTEND_URL}/docs/openapi`, 'openapi-url')}>
                  {copiedText === 'openapi-url' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <a href={`${FRONTEND_URL}/docs/swagger`} target="_blank" rel="noopener noreferrer">
            <Card className="hover-lift cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Code2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Swagger UI</p>
                    <p className="text-xs text-muted-foreground">Documentación interactiva</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </a>
          <a href={`${FRONTEND_URL}/docs/redoc`} target="_blank" rel="noopener noreferrer">
            <Card className="hover-lift cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Server className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">ReDoc</p>
                    <p className="text-xs text-muted-foreground">Vista alternativa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </a>
          <a href={`${FRONTEND_URL}/docs/openapi`} target="_blank" rel="noopener noreferrer">
            <Card className="hover-lift cursor-pointer h-full">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <FileJson className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="font-medium">OpenAPI JSON</p>
                    <p className="text-xs text-muted-foreground">Especificación completa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </a>
          <Card className="hover-lift cursor-pointer h-full" onClick={() => setShowWebhookDialog(true)}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Webhook className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="font-medium">Crear Webhook</p>
                  <p className="text-xs text-muted-foreground">Eventos en tiempo real</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="endpoints" className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full max-w-md">
            <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
            <TabsTrigger value="auth">Autenticación</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
            <TabsTrigger value="examples">Ejemplos</TabsTrigger>
          </TabsList>

          {/* Endpoints Tab */}
          <TabsContent value="endpoints" className="space-y-4">
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Input
                  placeholder="Buscar endpoints..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-4"
                />
              </div>
              <select 
                className="px-3 py-2 rounded-md border bg-background text-sm"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
              >
                <option value="all">Todos los grupos</option>
                {apiEndpoints.map(g => (
                  <option key={g.tag} value={g.tag}>{g.group}</option>
                ))}
              </select>
            </div>

            {/* Endpoint Groups */}
            <div className="space-y-4">
              {filteredEndpoints.map((group) => (
                <Card key={group.tag}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${group.color}`} />
                      <CardTitle className="text-base">{group.group}</CardTitle>
                      <Badge variant="outline" className="ml-auto">{group.endpoints.length} endpoints</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {group.endpoints.map((ep, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 group cursor-pointer"
                          onClick={() => copyToClipboard(ep.path, `${group.tag}-${idx}`)}
                        >
                          <Badge className={`font-mono text-xs px-2 ${methodColors[ep.method]}`}>
                            {ep.method}
                          </Badge>
                          <code className="text-sm flex-1 text-muted-foreground group-hover:text-foreground transition-colors">
                            {ep.path}
                          </code>
                          <span className="text-xs text-muted-foreground hidden sm:block">{ep.desc}</span>
                          {ep.auth ? (
                            <Key className="h-3 w-3 text-muted-foreground" title="Requiere autenticación" />
                          ) : (
                            <Globe className="h-3 w-3 text-green-500" title="Público" />
                          )}
                          {copiedText === `${group.tag}-${idx}` ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Auth Tab */}
          <TabsContent value="auth" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Key className="h-5 w-5" /> JWT Authentication
                </CardTitle>
                <CardDescription>
                  Todos los endpoints protegidos requieren un token JWT en el header Authorization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm font-medium mb-2">Header requerido:</p>
                  <pre className="p-3 bg-background rounded text-xs overflow-x-auto">
{`Authorization: Bearer <token>`}
                  </pre>
                </div>

                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm font-medium mb-2">Obtener token (Login con PIN):</p>
                  <pre className="p-3 bg-background rounded text-xs overflow-x-auto">
{`POST /api/auth/login/pin
Content-Type: application/json

{
  "restaurant_name": "Mi Restaurante",
  "pin": "1234"
}

// Respuesta exitosa
{
  "access_token": "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9...",
  "token_type": "bearer",
  "user": {
    "id": "...",
    "name": "...",
    "restaurant_id": "...",
    "role": "admin"
  }
}`}
                  </pre>
                </div>

                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4" /> Roles de Usuario
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                    {[
                      { role: 'super_admin', desc: 'Acceso completo a todo el sistema', color: 'bg-red-500/10 text-red-600' },
                      { role: 'owner', desc: 'Dueño del restaurante/cadena', color: 'bg-purple-500/10 text-purple-600' },
                      { role: 'admin', desc: 'Administrador de ubicación', color: 'bg-blue-500/10 text-blue-600' },
                      { role: 'manager', desc: 'Gestión de reservaciones', color: 'bg-green-500/10 text-green-600' },
                      { role: 'staff', desc: 'Operaciones básicas', color: 'bg-gray-500/10 text-gray-600' },
                    ].map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded bg-background">
                        <Badge className={r.color}>{r.role}</Badge>
                        <span className="text-xs text-muted-foreground">{r.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Códigos de Error</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { code: 400, desc: 'Bad Request - Datos inválidos' },
                    { code: 401, desc: 'Unauthorized - Token inválido o expirado' },
                    { code: 403, desc: 'Forbidden - Sin permisos suficientes' },
                    { code: 404, desc: 'Not Found - Recurso no encontrado' },
                    { code: 422, desc: 'Validation Error - Error de validación' },
                    { code: 500, desc: 'Server Error - Error interno' },
                  ].map((err, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                      <Badge variant="outline" className="font-mono">{err.code}</Badge>
                      <span className="text-sm text-muted-foreground">{err.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webhooks Tab */}
          <TabsContent value="webhooks" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="h-5 w-5" /> Webhooks Configurados
                    </CardTitle>
                    <CardDescription>
                      Recibe notificaciones en tiempo real de eventos en tu sistema
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowWebhookDialog(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Nuevo Webhook
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingWebhooks ? (
                  <div className="text-center py-8 text-muted-foreground">Cargando...</div>
                ) : webhooks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Webhook className="h-12 w-12 mx-auto mb-2 opacity-20" />
                    <p>No hay webhooks configurados</p>
                    <Button variant="outline" className="mt-4" onClick={() => setShowWebhookDialog(true)}>
                      Crear primer webhook
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {webhooks.map((wh) => (
                      <div key={wh.id} className="p-4 rounded-lg border bg-card">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {wh.is_active ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-muted-foreground" />
                              )}
                              <code className="text-sm truncate">{wh.url}</code>
                            </div>
                            {wh.description && (
                              <p className="text-xs text-muted-foreground mb-2">{wh.description}</p>
                            )}
                            <div className="flex flex-wrap gap-1">
                              {wh.events.slice(0, 3).map((ev, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{ev}</Badge>
                              ))}
                              {wh.events.length > 3 && (
                                <Badge variant="outline" className="text-xs">+{wh.events.length - 3} más</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3 text-green-500" /> {wh.success_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <XCircle className="h-3 w-3 text-red-500" /> {wh.failure_count}
                              </span>
                              {wh.last_triggered && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" /> {new Date(wh.last_triggered).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => handleTestWebhook(wh.id)}
                              title="Probar"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => handleDeleteWebhook(wh.id)}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Eventos Disponibles</CardTitle>
                <CardDescription>
                  Eventos que puedes suscribir en tus webhooks
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {webhookEvents.map((ev, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                      <code className="text-xs text-primary">{ev.event}</code>
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{ev.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Verificación de Firma</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`// Header enviado con cada webhook
X-OkReserva-Signature: sha256=<hmac_signature>
X-OkReserva-Event: <event_type>

// Verificar firma en tu servidor (Node.js)
const crypto = require('crypto');

function verifySignature(payload, signature, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

// Payload de ejemplo
{
  "event": "reservation.created",
  "timestamp": "2026-03-05T00:00:00Z",
  "data": {
    "id": "abc123",
    "guest_name": "Juan Pérez",
    "party_size": 4,
    "date": "2026-03-10",
    "time": "20:00"
  }
}`}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Examples Tab */}
          <TabsContent value="examples" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Crear Reservación</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`POST /api/reservations
Authorization: Bearer <token>
Content-Type: application/json

{
  "guest_name": "Juan Pérez",
  "guest_email": "juan@email.com",
  "guest_phone": "+52 555 123 4567",
  "party_size": 4,
  "date": "2026-03-15",
  "time": "20:00",
  "notes": "Mesa junto a la ventana",
  "table_id": "optional_table_id"
}

// Respuesta
{
  "id": "res_abc123",
  "status": "pending",
  "confirmation_code": "OK-ABC123",
  ...
}`}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Consultar Disponibilidad</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`GET /api/public/availability/{restaurant_id}?date=2026-03-15&party_size=4

// Respuesta
{
  "available_slots": [
    { "time": "18:00", "available": true },
    { "time": "18:30", "available": true },
    { "time": "19:00", "available": false },
    { "time": "19:30", "available": true },
    { "time": "20:00", "available": true }
  ],
  "waitlist_available": true
}`}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Predicción de No-Show</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`GET /api/predictions/no-show/{reservation_id}
Authorization: Bearer <token>

// Respuesta
{
  "risk_score": 0.35,
  "risk_level": "medium",
  "factors": {
    "customer_history": { "score": 0.2, "weight": 0.35 },
    "day_time": { "score": 0.4, "weight": 0.15 },
    "party_size": { "score": 0.3, "weight": 0.15 },
    "advance_booking": { "score": 0.5, "weight": 0.20 },
    "deposit": { "score": 0.0, "weight": 0.15 }
  },
  "recommendations": [
    "Enviar recordatorio 24h antes",
    "Considerar confirmación telefónica"
  ]
}`}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">WebSocket - Tiempo Real</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="p-4 bg-muted/50 rounded-lg text-xs overflow-x-auto">
{`// Conexión WebSocket
const ws = new WebSocket('wss://api.okreserva.com/ws/{restaurant_id}');

ws.onmessage = (event) => {
  const data = JSON.parse(event.data);
  
  switch(data.type) {
    case 'table_updated':
      // { id: "...", status: "seated" }
      break;
    case 'reservation_created':
      // { id: "...", guest_name: "..." }
      break;
    case 'waitlist_updated':
      // { action: "added", data: {...} }
      break;
  }
};`}
                </pre>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Rate Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Límites y Buenas Prácticas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium">Rate Limiting</p>
                <p className="text-muted-foreground">1000 req/min por IP</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium">Paginación</p>
                <p className="text-muted-foreground">Máx 500 items/request</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium">Formato de Fecha</p>
                <p className="text-muted-foreground">ISO 8601 (YYYY-MM-DD)</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium">Formato de Hora</p>
                <p className="text-muted-foreground">24h (HH:MM)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Webhook Dialog */}
      <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Webhook</DialogTitle>
            <DialogDescription>
              Configura un endpoint para recibir notificaciones de eventos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>URL del Webhook *</Label>
              <Input
                placeholder="https://tu-servidor.com/webhook"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Input
                placeholder="Ej: Notificaciones a Slack"
                value={webhookDescription}
                onChange={(e) => setWebhookDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Eventos a suscribir *</Label>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-lg">
                {webhookEvents.map((ev) => (
                  <div
                    key={ev.event}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      selectedEvents.includes(ev.event)
                        ? 'bg-primary/10 border border-primary'
                        : 'bg-muted/50 hover:bg-muted'
                    }`}
                    onClick={() => toggleEvent(ev.event)}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedEvents.includes(ev.event) ? 'bg-primary border-primary' : ''
                    }`}>
                      {selectedEvents.includes(ev.event) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono truncate">{ev.event}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedEvents.length} evento(s) seleccionado(s)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWebhookDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateWebhook} disabled={!webhookUrl || selectedEvents.length === 0}>
              Crear Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
